import type { ReactNode } from "react";
import type { SvgIconComponent } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { cn } from "@/utils/cn";
import { Breadcrumbs, type BreadcrumbItem } from "./Breadcrumbs";
import { PageTitleIcon } from "./PageTitleIcon";
import { resolveNavIconForPath } from "@/utils/pageNavIcon";
import { useActiveModule } from "@/hooks/useActiveModule";
import { getModuleAccentIconClass } from "@/config/appAccent";
import { buildBreadcrumbsFromPath } from "@/utils/breadcrumbs";
import { buildSettingsBreadcrumbs, isSettingsPath } from "@/utils/settingsBreadcrumbs";
import { localizeBreadcrumbItems, resolvePageHeading } from "@/i18n/navLabel";

function sanitizeBreadcrumbs(items: BreadcrumbItem[]): BreadcrumbItem[] {
  return items.filter((item) => !(item.label === "Org" && item.to === "/org"));
}

interface PageHeaderProps {
  /** Override when the page needs a dynamic title (e.g. entity name). Nav label wins when omitted. */
  title?: string;
  description?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  actions?: ReactNode;
  /** Override auto-resolved page icon from current route. Pass `null` to hide. */
  icon?: SvgIconComponent | null;
  /** When true (default), show the nav icon for the current pathname. */
  showIcon?: boolean;
  className?: string;
}

export function PageHeader({
  title,
  description: _description,
  breadcrumbs,
  actions,
  icon,
  showIcon = true,
  className,
}: PageHeaderProps) {
  const { t } = useTranslation("navigation");
  const location = useLocation();
  const activeModule = useActiveModule();
  const displayTitle = resolvePageHeading(title, location.pathname);
  const onSettingsRoute = isSettingsPath(location.pathname);
  const sanitized = breadcrumbs && !onSettingsRoute ? sanitizeBreadcrumbs(breadcrumbs) : [];
  const rawBreadcrumbs = onSettingsRoute
    ? buildSettingsBreadcrumbs(location.pathname, {
        activeModule,
        hash: location.hash.replace(/^#/, '') || undefined,
      })
    : sanitized.length > 0
      ? sanitized
      : buildBreadcrumbsFromPath(location.pathname, undefined, { activeModule });
  const displayBreadcrumbs = localizeBreadcrumbItems(rawBreadcrumbs, t, location.pathname);
  const resolvedIcon =
    icon !== undefined
      ? icon
      : showIcon
        ? resolveNavIconForPath(location.pathname)
        : null;
  const titleIconClass =
    activeModule && resolvedIcon
      ? getModuleAccentIconClass(activeModule)
      : undefined;

  return (
    <header className={cn("mb-6", className)}>
      {displayBreadcrumbs.length > 0 && (
        <div className="mb-2">
          <Breadcrumbs items={displayBreadcrumbs} />
        </div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
            {resolvedIcon ? (
              <PageTitleIcon icon={resolvedIcon} className={titleIconClass} />
            ) : null}
            <span className="min-w-0 truncate">{displayTitle}</span>
          </h1>
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>
    </header>
  );
}
